import { useEffect } from 'react'

import { css, keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'

import { AiChatBotFab } from './AiChatBotFab'
import { AiChatBotPanelContent } from './AiChatBotPanelContent'
import { usePanelAnimation } from './hooks/usePanelAnimation'
import { useAiChatBotEnabled } from '../connector/scrivito/useAiChatBotEnabled'
import { useAiChatBotStore } from '../core/store/useAiChatBotStore'
import { theme } from '@/Atomic/assets/theme'

const slideIn = keyframes`
  from { transform: translateX(calc(100% + 15px)); opacity: 0; }
  to   { transform: translateX(0); opacity: 1; }
`

const slideOut = keyframes`
  from { transform: translateX(0);                 opacity: 1; }
  65%  { transform: translateX(60%);               opacity: 1; }
  to   { transform: translateX(calc(100% + 15px)); opacity: 0; }
`

const FloatingRoot = styled.div`
  position: fixed;
  inset: 0;
  /* Above Bootstrap modal tier (1055) so the panel overlays page content */
  z-index: 1200;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  pointer-events: none;
`

const FloatingOverlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: all;
`

const FloatingPanel = styled.div<{ isClosing: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 420px;
  height: calc(100vh - 30px);
  margin: 15px;
  background: ${theme.colors.background.default};
  border-radius: ${theme.radii.xl};
  box-shadow: var(--jr-offcanvas-box-shadow);
  overflow: hidden;
  pointer-events: all;
  z-index: 1;
  animation: ${({ isClosing }) =>
    isClosing
      ? css`
          ${slideOut} 0.45s cubic-bezier(0.4, 0, 0.8, 0.6) forwards
        `
      : css`
          ${slideIn} 0.4s cubic-bezier(0.22, 1, 0.36, 1) forwards
        `};
`

export function AiChatBot({ headless = false }: { headless?: boolean } = {}) {
  const { t } = useTranslation()
  const enabled = useAiChatBotEnabled()
  const { show, isClosing, onAnimationEnd } = usePanelAnimation()
  const { close, setMode } = useAiChatBotStore.getState()

  useEffect(() => {
    setMode(headless ? 'headless' : 'default')
  }, [headless, setMode])

  if (!enabled || headless) return null

  return (
    <>
      <AiChatBotFab />
      {show && (
        <FloatingRoot
          role="dialog"
          aria-modal="true"
          aria-label={t('components.aiChatBot.title')}
        >
          <FloatingOverlay onClick={close} aria-hidden="true" />
          <FloatingPanel isClosing={isClosing} onAnimationEnd={onAnimationEnd}>
            <AiChatBotPanelContent onClose={close} />
          </FloatingPanel>
        </FloatingRoot>
      )}
    </>
  )
}
